import { useEffect, useMemo, useRef, useState, type RefObject, type KeyboardEvent, type ChangeEvent } from "react";
import SVG from "react-inlinesvg";
import ActionMenu, { type ActionMenuAction } from "src/components/ActionMenu";
import IconButton from "src/components/IconButton";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { Input } from "src/sudokit/components/Input";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { cn } from "../../../lib/cn";
import { useProjectStore } from "../../../stores/projectStore";
import { useFindAndReplaceState, SCOPE_OPTIONS, SCOPE_STORY_BIBLE, type EditorWrapper } from "./useFindAndReplaceState";

interface ColumnWidths {
  right?: {
    current?: number;
  };
}

interface FindAndReplaceProps {
  editor: EditorWrapper | null | undefined;
  documentId?: string;
  columnWidths?: ColumnWidths;
  editorHostNode?: HTMLElement | null;
  scrollHostRef?: RefObject<HTMLElement | null>;
  close: () => void;
}

export const FindAndReplace = ({
  editor,
  documentId,
  columnWidths,
  editorHostNode,
  scrollHostRef,
  close: _close,
}: FindAndReplaceProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [resultsExpanded, setResultsExpanded] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const defaultInputRef = useRef<HTMLInputElement>(null);
  const setDocumentId = useProjectStore((state: any) => state.setDocumentId);

  const {
    // State
    searchTerm,
    replaceTerm,
    scope,
    backendDocumentResults,
    isSearchingBackend,
    isReplacingBackend,
    replaceProgress,
    // Computed
    hasGlobalSearch,
    hasStoryBibleSearchables,
    searchResultSummary,
    canReplace,
    // Actions
    handleSearchTermChange,
    handleReplaceTermChange,
    handleScopeChange,
    handleNext,
    handlePrevious,
    replaceCurrentResult,
    handleReplaceAll,
    clearSearchState,
  } = useFindAndReplaceState({ editor, documentId, editorHostNode, scrollHostRef });

  // Filter scope options: only show Story Bible when Story Bible searchables are registered
  const availableScopeOptions = useMemo(
    () => SCOPE_OPTIONS.filter(
      (option) => option.value !== SCOPE_STORY_BIBLE || hasStoryBibleSearchables
    ),
    [hasStoryBibleSearchables]
  );

  const scopeActions = useMemo<Record<string, ActionMenuAction>>(
    () =>
      availableScopeOptions.reduce<Record<string, ActionMenuAction>>((acc, option) => {
        acc[option.value] = {
          label: option.label,
          icon: <SVG src={option.icon.src} width={20} height={20} />,
          active: option.value === scope,
          onClick: () => handleScopeChange(option.value),
        };
        return acc;
      }, {}),
    [handleScopeChange, availableScopeOptions, scope]
  );

  const currentScopeLabel = SCOPE_OPTIONS.find((opt) => opt.value === scope)?.label ?? "";

  // Compensate position when they resize column widths
  useEffect(() => {
    if (containerRef.current) {
      const right = (columnWidths?.right?.current || 0) + 45;
      containerRef.current.style.right = `${right}px`;
    }
  }, [columnWidths?.right?.current]);

  // Focus input on mount, clear state on unmount.
  // clearSearchState is held in a ref so this effect only runs once — otherwise
  // every editor swap (e.g. switching documents from a result row) would run the
  // cleanup and wipe backendDocumentResults out from under the user.
  const clearSearchStateRef = useRef(clearSearchState);
  clearSearchStateRef.current = clearSearchState;
  useEffect(() => {
    defaultInputRef.current?.focus();
    return () => clearSearchStateRef.current();
  }, []);

  const close = () => {
    if (!containerRef.current) return;
    containerRef.current.classList.remove("animate-in", "fade-in-0", "slide-in-from-right-4");
    containerRef.current.classList.add("animate-out", "fade-out-0", "slide-out-to-right-4");

    setTimeout(() => {
      if (!containerRef.current) return;
      containerRef.current.classList.add("animate-in", "fade-in-0", "slide-in-from-right-4");
      containerRef.current.classList.remove("animate-out", "fade-out-0", "slide-out-to-right-4");
      if (editor && editor.tiptap) {
        editor.tiptap.commands.setSelectedSearchResult(-1);
      }
      _close();
    }, 120);
  };

  const handleEscapeToClose = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      close();
    }
    if (e.key === "Enter") {
      handleNext();
    }
    if (e.key === "f" && (e.ctrlKey || e.metaKey)) {
      close();
    }
  };

  const onSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleSearchTermChange(e.target.value);
  };

  const onReplaceInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    handleReplaceTermChange(e.target.value);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <WithReset
      ref={containerRef}
      className={cn(
        "bg-app-menu-color rounded-xl px-1.5 shadow-card w-[420px] fixed top-[118px] z-[10000] animate-in fade-in-0 slide-in-from-right-8 flex flex-col transition-all",
        isExpanded ? "py-2" : "py-1"
      )}
    >
      <div className="flex flex-col w-full gap-0.5">
        <div className="flex flex-row items-center w-full gap-1">
          <div>
            <IconButton
              onClick={toggleExpanded}
              aria-label={isExpanded ? "Collapse" : "Expand"}
              src="/assets/chevron-right.svg"
              iconSize={16}
              className={cn("transition-all", isExpanded ? "rotate-90" : "rotate-0")}
            />
          </div>

          <WithReset className="relative w-full">
            <Input
              mode={isExpanded ? "default" : "ghost"}
              placeholder="Find"
              ref={defaultInputRef}
              value={searchTerm}
              onChange={onSearchInputChange}
              onKeyDown={handleEscapeToClose}
              maxLength={200}
              className="text-sm py-1.5"
            />
          </WithReset>

          <div className="flex gap-0 items-center justify-end">
            <WithReset className="flex items-center">
              <IconButton
                onClick={handlePrevious}
                src="/assets/back-arrow.svg"
                className="rotate-90 [&_path]:stroke-app-grey-0"
                aria-label="Previous match"
                iconSize={16}
              />

              <IconButton
                onClick={handleNext}
                src="/assets/back-arrow.svg"
                className="-rotate-90  [&_path]:stroke-app-grey-0 -ml-2.5"
                aria-label="Next match"
                iconSize={16}
              />
            </WithReset>

            <IconButton
              onClick={() => close()}
              src="/assets/close.svg"
              aria-label="Close"
              className="-ml-1"
              iconSize={22}
            />
          </div>
        </div>

        {/* Result summary shown below input */}
        {searchResultSummary && <div className="text-xs text-app-grey-1 pl-8 ml-3 pb-0.5 animate-in fade-in truncate">{searchResultSummary}</div>}
      </div>

      {isExpanded && (
        <>
          <WithReset className="relative flex items-center justify-start w-full gap-1">
            <IconButton
              src="/assets/chevron-right.svg"
              iconSize={16}
              className={cn("opacity-0 select-none")}
            />
            <Input
              placeholder="Replace"
              value={replaceTerm}
              onChange={onReplaceInputChange}
              onKeyDown={handleEscapeToClose}
              maxLength={200}
              className="text-sm py-1.5 w-full"
              containerClassName="w-full"
            />
          </WithReset>

          <div className="flex flex-row justify-between items-center gap-1 pt-1">
            {/* Scope selector */}
            {hasGlobalSearch && (
              <div className="scope-selector pl-3 flex items-center">
                <span className="text-app-grey-1 text-sm font-medium mr-1">Find in</span>
                <ActionMenu
                  actions={scopeActions}
                  position="bottom-start"
                >
                  {(openMenu) => (
                    <button
                      type="button"
                      onClick={openMenu}
                      aria-label="Search scope"
                      className="flex items-center gap-0.5 text-app-primary-cta text-sm font-semibold hover:opacity-80 bg-transparent border-0 p-0 cursor-pointer"
                    >
                      <span>{currentScopeLabel}</span>
                      <SVG
                        src="/assets/chevron-down.svg"
                        width={12}
                        height={12}
                        className="[&_path]:fill-app-primary-cta"
                      />
                    </button>
                  )}
                </ActionMenu>
              </div>
            )}

            <div className="flex flex-row gap-1 ml-auto">
              <SudoButton
                variant="tertiary"
                size="small"
                onClick={replaceCurrentResult}
                disabled={!canReplace || isReplacingBackend}
                className="max-w-[6.5rem]"
              >
                Replace
              </SudoButton>

              <SudoButton
                variant="tertiary"
                size="small"
                onClick={handleReplaceAll}
                disabled={!canReplace || isSearchingBackend || isReplacingBackend}
                className="max-w-[6.5rem]"
              >
                {isReplacingBackend ? (replaceProgress ? `Replacing ${replaceProgress.completed}/${replaceProgress.total}` : "Replacing...") : "Replace All"}
              </SudoButton>
            </div>
          </div>

          {/* Backend document results panel */}
          {backendDocumentResults && backendDocumentResults.totalDocuments > 0 && (
            <div className="border-t border-app-grey-3 mt-2 pt-2">
              <button
                onClick={() => setResultsExpanded(!resultsExpanded)}
                className="flex items-center justify-between w-full text-sm text-white px-3 py-1 rounded-md"
              >
                <span>
                  {backendDocumentResults.totalMatches} matches in {backendDocumentResults.totalDocuments} {backendDocumentResults.totalDocuments === 1 ? "document" : "documents"}
                </span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={cn("transition-transform", resultsExpanded ? "rotate-180" : "rotate-0")}
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {resultsExpanded && (
                <div className="max-h-[200px] overflow-y-auto px-3 py-1 text-xs">
                  {backendDocumentResults.results.map((result) => (
                    <button
                      key={result.documentId}
                      type="button"
                      onClick={() => setDocumentId(result.documentId)}
                      className={`flex justify-between items-center w-full py-1 px-2 rounded-md text-app-grey-1 text-left text-[11px] bg-transparent! hover:${result.documentId !== documentId ? "bg-app-menu-color-hover" : ""}  ${result.documentId === documentId ? "bg-app-selected-element-background" : ""}`}
                      style={{backgroundColor: 'transparent'}}
                    >
                      <span className="truncate flex-1 mr-2">{result.title}</span>
                      <span className="text-app-grey-2 whitespace-nowrap">
                        {result.matchCount} {result.matchCount === 1 ? "match" : "matches"}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </WithReset>
  );
};
